import i18n from 'helpers/i18n';
import messages from './messages';
import { clsx } from 'clsx';
import { useState } from 'react';
import { TextButton } from 'components/Organization/TextButton';
import { PrimaryButton } from 'components/Organization/PrimaryButton';

interface EditableFieldsGroupProps {
  fields: {
    label: string;
    value: React.ReactNode;
    initialValue?: any;
    control?: (options: {
      value: any;
      setValue: (v: any) => void;
      internalValues: any[];
      index: number;
    }) => React.ReactNode;
  }[];

  showLabel?: boolean;
  border?: boolean;
  className?: string;
  disabled?: boolean;
  error?: string;
  onSave?: (values: any[]) => void | boolean | Promise<void | boolean>;
  onCancel?: (error: string) => void;
}

export function EditableFieldsGroup({
  fields,
  showLabel = true,
  onSave,
  onCancel,
  className,
  border = true,
  disabled = false,
  error
}: EditableFieldsGroupProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [internalValues, setInternalValues] = useState(
    fields.map(f => f.initialValue)
  );

  const classes = clsx(
    'flex py-6 gap-8 justify-between',
    border && 'border-t border-[#D7DFE8]',
    className
  );

  const handleSave = async () => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      const result = await Promise.resolve(onSave?.(internalValues));
      setIsSaving(false);

      if (typeof result !== 'boolean' || result) setIsEditing(false);
    } catch (error) {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isSaving) return;
    onCancel?.('');
    setInternalValues(fields.map(f => f.initialValue));
    setIsEditing(false);
  };

  const setFieldValue = (index: number, value: any) => {
    setInternalValues(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  return (
    <div className={classes}>
      {isEditing ? (
        <div className="flex flex-col w-full gap-4">
          <div className="flex flex-col gap-2">
            {fields.map((field, idx) => (
              <div key={idx}>
                {field.control?.({
                  value: internalValues[idx],
                  setValue: v => setFieldValue(idx, v),
                  internalValues,
                  index: idx
                })}
              </div>
            ))}
            {error && (
              <p className="font-semibold text-sm text-error m-0 px-1">
                {error}
              </p>
            )}
          </div>
          <div className="flex justify-end gap-6">
            <TextButton size="small" onClick={handleCancel}>
              {i18n.ft(messages.cancel)}
            </TextButton>
            <PrimaryButton size="small" onClick={handleSave}>
              {i18n.ft(messages.save)}
            </PrimaryButton>
          </div>
        </div>
      ) : (
        <div className="flex flex-col">
          {fields.map((field, idx) => (
            <div key={idx} className={clsx(idx === 0 ? 'pb-6' : 'pt-6')}>
              {showLabel ? (
                <div className="text-base font-semibold">{field.label}</div>
              ) : (
                <div className="sr-only">{field.label}</div>
              )}
              <div className="mt-1.5 text-base">{field.value || '--'}</div>
            </div>
          ))}
        </div>
      )}

      {fields.some(f => f.control) && !isEditing && !disabled && (
        <button
          onClick={() => setIsEditing(true)}
          className="flex items-center justify-center text-action w-10 h-10 shrink-0"
        >
          <i className="fa-solid fa-pencil" />
        </button>
      )}
    </div>
  );
}
